/** A point on the map with how many of the query's people are there. */
export interface WeightedPoint {
  lat: number;
  lng: number;
  weight: number;
}

/** A box on the map: [[west, south], [east, north]], as Mapbox's fitBounds takes it. */
export type MapBounds = [[number, number], [number, number]];

/** An end point is an outlier when it holds under this share of the people... */
const OUTLIER_SHARE = 0.05;
/** ...and its gap to the next point is over this share of the span (far, not just light). */
const OUTLIER_GAP = 0.25;
/** A box is at least this wide and tall (degrees), so one account is not zoomed in on. */
const MIN_SPAN = 0.2;

/**
 * The range of the values less their outliers at either end: the run of points at an end goes
 * while it is light (under OUTLIER_SHARE of the weight together) and far from the rest (the gap
 * after it over OUTLIER_GAP of the span); the points sorted by value. A few matches in Alaska
 * and Hawaii go, Texas's outermost districts stay.
 */
function trimmedRange(sorted: { value: number; weight: number }[]): [number, number] {
  const total = sorted.reduce((sum, p) => sum + p.weight, 0);
  let lo = 0;
  let hi = sorted.length - 1;
  // The end of the light run from an end (step 1 from lo, -1 from hi) that a big gap follows,
  // or the end itself when there is none.
  const afterOutliers = (from: number, step: 1 | -1): number => {
    const span = sorted[hi].value - sorted[lo].value;
    let weight = 0;
    let cut = from;
    for (let i = from; i !== (step === 1 ? hi : lo); i += step) {
      weight += sorted[i].weight;
      if (weight >= total * OUTLIER_SHARE) break;
      if (Math.abs(sorted[i + step].value - sorted[i].value) > span * OUTLIER_GAP) cut = i + step;
    }
    return cut;
  };
  for (;;) {
    if (hi - lo < 2) break;
    const nextLo = afterOutliers(lo, 1);
    const nextHi = afterOutliers(hi, -1);
    if (nextLo === lo && nextHi === hi) break;
    lo = nextLo;
    hi = nextHi;
  }
  return [sorted[lo].value, sorted[hi].value];
}

/**
 * The box to fit the map to for the query's people: every account they are at, less the few
 * far from the rest, so a stray match does not pull the view off where the people are. A point
 * with no location is left out; a point's weight is its people, so a big account counts for
 * more than a small one. Null when no point has a location.
 */
export function focusBounds(points: WeightedPoint[]): MapBounds | null {
  const located = points.filter((p) => (p.lat !== 0 || p.lng !== 0) && p.weight > 0);
  if (located.length === 0) return null;
  const byValue = (value: (p: WeightedPoint) => number) =>
    located.map((p) => ({ value: value(p), weight: p.weight })).sort((a, b) => a.value - b.value);
  let [west, east] = trimmedRange(byValue((p) => p.lng));
  let [south, north] = trimmedRange(byValue((p) => p.lat));
  if (east - west < MIN_SPAN) {
    const mid = (east + west) / 2;
    west = mid - MIN_SPAN / 2;
    east = mid + MIN_SPAN / 2;
  }
  if (north - south < MIN_SPAN) {
    const mid = (north + south) / 2;
    south = Math.max(-90, mid - MIN_SPAN / 2);
    north = Math.min(90, mid + MIN_SPAN / 2);
  }
  return [
    [west, south],
    [east, north],
  ];
}
